// A long list a page at a time: which rows are showing, of how many, and the
// way to the others. The service pages a list that grows with the fleet
// (useServerPages), and says how many there are in all; a short list that is
// loaded whole is a window onto itself (usePages). A list that fits on one
// page has no footer.

import { Group, Pagination, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

export const PAGE_SIZE = 25;

/**
 * The rows of the page in view, and the footer that moves between pages.
 * `resetOn` is whatever narrows the list (a search, a filter): when it
 * changes, the list starts again from its first page.
 */
export function usePages<T>(rows: readonly T[], noun: string, resetOn = ''): { shown: T[]; footer: ReactNode } {
  const [at, setAt] = useState({ page: 1, key: resetOn });
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // A changed search is read during render, so the first page shows at once; a list that shrank keeps a page that exists.
  const page = at.key === resetOn ? Math.min(at.page, pages) : 1;
  const from = (page - 1) * PAGE_SIZE;
  const shown = rows.slice(from, from + PAGE_SIZE);
  const footer = <PageFooter page={page} total={rows.length} noun={noun} onChange={(p) => setAt({ page: p, key: resetOn })} />;
  return { shown, footer };
}

/**
 * A page of a list the service pages, by number. `key` names the read (its
 * first part is what a change invalidates); `narrowed` is whatever narrows
 * the list (a search, a filter), and when it changes the list starts again
 * from its first page. The rows on screen stay while the next page loads.
 */
export function useServerPages<R extends { total: number }>({ key, narrowed, read, noun, refetchInterval, enabled = true }: {
  key: readonly unknown[];
  narrowed: string;
  read: (page: { pageSize: number; offset: number }) => Promise<R>;
  noun: string;
  /** How often to read it again: a number of milliseconds, false for never, or decided from the page as last read. */
  refetchInterval?: number | false | ((data: R | undefined) => number | false);
  enabled?: boolean;
}) {
  const [at, setAt] = useState({ page: 1, key: narrowed });
  const page = at.key === narrowed ? at.page : 1;
  const query = useQuery({
    queryKey: [...key, narrowed, page],
    queryFn: () => read({ pageSize: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    placeholderData: keepPreviousData,
    enabled,
    ...(refetchInterval !== undefined ? { refetchInterval: typeof refetchInterval === 'function' ? (q: { state: { data?: R | undefined } }) => refetchInterval(q.state.data) : refetchInterval } : {}),
  });
  const total = query.data?.total ?? 0;
  const setPage = (p: number) => setAt({ page: p, key: narrowed });
  // A list that shrank under the page in view (its last row removed) keeps a page that exists.
  const last = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (query.data && !query.isPlaceholderData && page > last) setPage(last);
  return { query, page, total, setPage, footer: <PageFooter page={page} total={total} noun={noun} onChange={setPage} /> };
}

/** The footer by itself, for a list the service pages: which rows are showing of how many, and the way to the others. Nothing when it all fits on one page. */
export function PageFooter({ page, total, noun, onChange }: { page: number; total: number; noun: string; onChange: (page: number) => void }) {
  if (total <= PAGE_SIZE) return null;
  const from = (page - 1) * PAGE_SIZE;
  return (
    <Group justify="space-between" align="center" gap="md" wrap="wrap" px="lg" py="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Text size="sm" c="dimmed" className="num" role="status">{from + 1} to {Math.min(from + PAGE_SIZE, total)} of {total} {noun}</Text>
      <Pagination
        size="sm"
        total={Math.ceil(total / PAGE_SIZE)}
        value={page}
        onChange={onChange}
        getControlProps={(control) => ({ 'aria-label': control === 'previous' ? 'Previous page' : control === 'next' ? 'Next page' : control === 'first' ? 'First page' : 'Last page' })}
        getItemProps={(p) => ({ 'aria-label': `Page ${p}` })}
      />
    </Group>
  );
}
